import { appConfig } from "@/config";
import { fetchPlatformVersion, type PlatformVersionApi } from "@/lib/api";
import { LABEL } from "@aisecure/status";

export type BuildInfoRow = {
  label: string;
  value: string;
};

const UNAVAILABLE = LABEL.notAvailable;

function displayValue(
  raw: string | null | undefined,
  fallbacks: Array<string | null | undefined> = [],
): string {
  const candidates = [raw, ...fallbacks];
  for (const candidate of candidates) {
    const text = (candidate || "").trim();
    if (!text) continue;
    const lower = text.toLowerCase();
    if (lower === "unknown" || lower === "unavailable" || lower === "none" || lower === "null") {
      continue;
    }
    return text;
  }
  return UNAVAILABLE;
}

function localOpsFallback(): PlatformVersionApi["operations_dashboard"] {
  return {
    name: "operations-dashboard",
    git_commit: displayValue(appConfig.gitCommit),
    git_branch: displayValue(appConfig.gitBranch),
    build_version: displayValue(appConfig.buildVersion, [appConfig.version]),
    build_timestamp: displayValue(appConfig.buildTimestamp),
    github_run_id: displayValue(appConfig.githubRunId),
    image_tag: displayValue(appConfig.imageTag, [
      appConfig.version ? `nvs-operations-dashboard:${appConfig.version}` : "",
    ]),
    image_digest: UNAVAILABLE,
    version: displayValue(appConfig.version, [appConfig.buildVersion]),
  };
}

/** Prefer API metadata; merge baked Vite /version.json fallbacks for Ops fields. */
export async function loadBuildInfoRows(): Promise<BuildInfoRow[]> {
  let api: PlatformVersionApi | null = null;
  try {
    api = await fetchPlatformVersion();
  } catch {
    api = null;
  }

  let staticOps: Partial<PlatformVersionApi["operations_dashboard"]> & {
    github_run_id?: string;
  } | null = null;
  try {
    const res = await fetch("/version.json", { headers: { Accept: "application/json" } });
    if (res.ok) {
      staticOps = (await res.json()) as Partial<PlatformVersionApi["operations_dashboard"]>;
    }
  } catch {
    staticOps = null;
  }

  const local = localOpsFallback();
  const cp = api?.control_plane;
  const ops = api?.operations_dashboard;

  return [
    {
      label: "Git commit",
      value: displayValue(api?.git_commit, [
        ops?.git_commit,
        staticOps?.git_commit as string,
        local.git_commit,
        cp?.git_commit,
      ]),
    },
    {
      label: "Git branch",
      value: displayValue(api?.git_branch, [
        ops?.git_branch,
        staticOps?.git_branch as string,
        local.git_branch,
        cp?.git_branch,
      ]),
    },
    {
      label: "Build timestamp",
      value: displayValue(api?.build_timestamp, [
        ops?.build_timestamp,
        staticOps?.build_timestamp as string,
        local.build_timestamp,
        cp?.build_timestamp,
      ]),
    },
    {
      label: "GitHub Actions run-id",
      value: displayValue(api?.github_run_id, [
        ops?.github_run_id,
        staticOps?.github_run_id,
        local.github_run_id,
        cp?.github_run_id,
      ]),
    },
    {
      label: "Build version",
      value: displayValue(ops?.build_version, [
        staticOps?.build_version as string,
        local.build_version,
        cp?.build_version,
      ]),
    },
    {
      label: "Docker image tag",
      value: displayValue(ops?.image_tag, [staticOps?.image_tag as string, local.image_tag]),
    },
    {
      label: "Docker image digest",
      value: displayValue(ops?.image_digest),
    },
    {
      label: "Control Plane version",
      value: displayValue(cp?.version, [cp?.build_version, cp?.git_commit]),
    },
    {
      label: "Operations Dashboard version",
      value: displayValue(ops?.version, [
        staticOps?.version as string,
        local.version,
        appConfig.version,
      ]),
    },
  ];
}
